/**
 * Developers · Create API key sheet — re-skin of the DevelopersSection create
 * form. Side Sheet only. The actual create call lives in the parent (the real
 * useApiKeys hook); this just collects a name and reports it up.
 */
import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";

interface DevelopersCreateKeySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creating: boolean;
  onCreate: (name: string) => void;
}

const INPUT_CLASS =
  "h-[46px] w-full px-3.5 rounded-xl border border-genie-border bg-white text-[14.5px] text-[#222B45] focus:outline-none focus:border-genie-primary focus:ring-2 focus:ring-genie-primary/15 transition-all";

export const DevelopersCreateKeySheet = ({
  open,
  onOpenChange,
  creating,
  onCreate,
}: DevelopersCreateKeySheetProps) => {
  const [name, setName] = useState("");

  useEffect(() => {
    if (!open) setName("");
  }, [open]);

  const submit = () => {
    if (!name.trim()) {
      toast.error("Please enter a name for your key.");
      return;
    }
    onCreate(name.trim());
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showClose={false}
        className="w-full sm:max-w-md flex flex-col h-full p-0 bg-genie-background"
      >
        {/* header */}
        <div className="h-14 flex-shrink-0 px-6 bg-white border-b border-genie-border flex items-center justify-between">
          <h3 className="text-[17px] font-extrabold text-[#222B45] tracking-[-0.3px]">
            Create API key
          </h3>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="w-8 h-8 rounded-full inline-flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-[#222B45] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-auto px-6 py-6">
          <div className="rounded-[18px] border border-genie-border bg-white shadow-sm px-5 py-5">
            <label
              htmlFor="hg-key-name"
              className="block text-[12.5px] font-semibold text-[#64748B] mb-2"
            >
              Key name
            </label>
            <input
              id="hg-key-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
              }}
              placeholder='e.g. "Production", "Staging", "Local testing"'
              maxLength={100}
              autoFocus
              className={INPUT_CLASS}
            />
            <p className="text-[12px] text-[#64748B] mt-2.5 leading-relaxed">
              Give your key a descriptive name so you can recognise it later.
              You'll see the full key once, right after it's created.
            </p>
          </div>
        </div>

        {/* footer */}
        <div className="flex-shrink-0 px-6 py-4 bg-white border-t border-genie-border">
          <button
            type="button"
            onClick={submit}
            disabled={creating || !name.trim()}
            className="w-full h-11 rounded-full inline-flex items-center justify-center gap-2 text-[14px] font-bold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {creating && <Loader2 className="w-4 h-4 animate-spin" />}
            {creating ? "Creating..." : "Create key"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DevelopersCreateKeySheet;
